/**
 * Event Log API Client
 * 
 * API-Methoden für Event-Log System
 */

import apiClient from './api';

export interface EventLog {
  id: string;
  user_id: string;
  event_type: string;
  event_category: string;
  event_data?: any;
  is_read: boolean;
  created_at: string;
}

export interface EventLogFilter {
  category?: string;
  type?: string;
  type_prefix?: string;
  is_read?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Event-Log abrufen
 */
export const getEventLog = async (userId: string, filter?: EventLogFilter): Promise<{ events: EventLog[]; total: number }> => {
  const params = new URLSearchParams();
  if (filter?.category) params.append('category', filter.category);
  if (filter?.type) params.append('type', filter.type);
  if (filter?.type_prefix) params.append('type_prefix', filter.type_prefix);
  if (filter?.is_read !== undefined) params.append('is_read', String(filter.is_read));
  if (filter?.limit) params.append('limit', String(filter.limit));
  if (filter?.offset) params.append('offset', String(filter.offset));

  const queryString = params.toString();
  const url = `/event-log/${userId}${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<{ success: boolean; data?: { events: EventLog[]; total: number }; error?: { message?: string } }>(url);
  const body = response.data;
  if (body?.success && body?.data) {
    return body.data;
  }
  throw new Error((body as any)?.error?.message || 'Fehler beim Abrufen des Event-Logs');
};

/**
 * Anzahl neuer Events abrufen
 */
export const getNewEventCount = async (userId: string): Promise<number> => {
  const response = await apiClient.get<{ success: boolean; data?: { count: number }; error?: { message?: string } }>(`/event-log/new-count/${userId}`);
  const body = response.data;
  if (body?.success && body?.data != null) {
    return body.data.count;
  }
  throw new Error((body as any)?.error?.message || 'Fehler beim Abrufen der Event-Anzahl');
};

/**
 * Events per Filter löschen (Bulk-Delete)
 */
export const deleteEventsByFilter = async (
  userId: string,
  filter: { type_prefix?: string; category?: string; type?: string }
): Promise<number> => {
  const response = await apiClient.post<{ success: boolean; data?: { deleted: number }; error?: { message?: string } }>(
    `/event-log/delete-filter/${userId}`,
    filter
  );
  const body = response.data;
  if (body?.success) return body.data?.deleted ?? 0;
  throw new Error(body?.error?.message || 'Fehler beim Löschen der Events');
};

/**
 * Events als gelesen markieren
 */
export const markEventsAsRead = async (userId: string, eventIds?: string[]): Promise<void> => {
  const response = await apiClient.post<{ success: boolean; error?: { message?: string } }>(`/event-log/mark-as-read/${userId}`, { eventIds });
  const body = response.data;
  if (!body?.success) {
    throw new Error(body?.error?.message || 'Fehler beim Markieren der Events');
  }
};
